import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Card } from '../atoms/Card/Card';
import { Grid } from '../atoms/Grid/Grid';
import { Inline } from '../atoms/Inline/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { REEF_GUARD_FINDINGS_KEY } from '../../services/reefGuardService';
import {
  listTestIntervals,
  resetTestInterval,
  saveTestInterval,
  testIntervalsKey,
  type TestInterval,
} from '../../services/testIntervalService';

/** Interval choices in days (the API takes hours, 1–720). */
const DAY_OPTIONS = [1, 2, 3, 4, 5, 7, 10, 14, 21, 30] as const;
const DAY_MS = 86_400_000;

function formatDays(hours: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(hours / 24);
}

/** Select value of an interval: 'default', 'off' or the hours. */
function selectValue(interval: TestInterval): string {
  if (interval.mode === 'default') return 'default';
  if (interval.mode === 'off' || interval.intervalHours === null) return 'off';
  return String(interval.intervalHours);
}

function IntervalCard({ interval, onChange, busy }: {
  interval: TestInterval;
  onChange: (parameterId: string, value: string) => void;
  busy: boolean;
}) {
  const { t, i18n } = useTranslation();
  const days = (hours: number) => formatDays(hours, i18n.language);
  const name = String(t(`measurement.history.parameterNames.${interval.parameterId}`));
  const options = [
    // Plural-aware: "Default (daily)" for 24 h, "Default (every 3.5 days)" otherwise.
    { value: 'default', label: String(t('testIntervals.option.default', { count: interval.defaultHours / 24, days: days(interval.defaultHours) })) },
    ...DAY_OPTIONS.map((d) => ({ value: String(d * 24), label: String(t('testIntervals.option.days', { count: d, days: d })) })),
    { value: 'off', label: String(t('testIntervals.option.off')) },
  ];
  const current = selectValue(interval);
  if (!options.some((o) => o.value === current) && interval.intervalHours !== null) {
    options.splice(1, 0, { value: current, label: String(t('testIntervals.option.custom', { count: interval.intervalHours / 24, days: days(interval.intervalHours) })) });
  }
  const lastDays = interval.lastTestedAt === null ? null : Math.max(0, Math.floor((Date.now() - new Date(interval.lastTestedAt).getTime()) / DAY_MS));
  const dueLabel = interval.dueAt === null
    ? null
    : new Intl.DateTimeFormat(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(interval.dueAt));

  return (
    <li className="test-intervals__item" data-testid={`test-interval-${interval.parameterId}`}>
      <Card>
        <Stack gap="2">
          <Select
            label={String(t('testIntervals.intervalFor', { parameter: name }))}
            value={current}
            options={options}
            disabled={busy}
            onChange={(event) => onChange(interval.parameterId, event.target.value)}
            data-testid="test-interval-select"
          />
          {interval.mode === 'default' && interval.basis !== 'default' && (
            <Text color="muted" variant="caption">{t(`testIntervals.basis.${interval.basis}`)}</Text>
          )}
          <Text color="muted" variant="caption" data-testid="test-interval-last">
            {lastDays === null
              ? t('testIntervals.noRecentTest')
              : lastDays === 0
                ? t('testIntervals.lastToday')
                : t('testIntervals.lastDaysAgo', { count: lastDays, days: lastDays })}
            {dueLabel !== null && !interval.overdue && ` · ${String(t('testIntervals.nextDue', { date: dueLabel }))}`}
          </Text>
          {(interval.overdue || interval.dosingActive) && (
            <Inline gap="2" wrap>
              {interval.overdue && <Badge tone="warning" data-testid="test-interval-overdue">{t('testIntervals.overdue')}</Badge>}
              {interval.dosingActive && <Badge tone="info">{t('testIntervals.dosing')}</Badge>}
            </Inline>
          )}
        </Stack>
      </Card>
    </li>
  );
}

/**
 * "Test-Intervalle" (Reef Guard #1823): how often each parameter should be
 * tested. A test past its interval shows up as a Reef Guard hint (a warning
 * while the parameter is dosed) and in the daily task e-mail.
 */
export function TestIntervalsSection({ aquariumId }: { aquariumId: string }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [announcement, setAnnouncement] = useState('');
  const query = useQuery({ queryKey: testIntervalsKey(aquariumId), queryFn: () => listTestIntervals(aquariumId), enabled: Boolean(aquariumId) });
  const save = useMutation({
    mutationFn: ({ parameterId, value }: { parameterId: string; value: string }) =>
      value === 'default'
        ? resetTestInterval(aquariumId, parameterId)
        : saveTestInterval(aquariumId, parameterId, value === 'off' ? null : Number(value)),
    onSuccess: (data, { parameterId }) => {
      queryClient.setQueryData(testIntervalsKey(aquariumId), data);
      void queryClient.invalidateQueries({ queryKey: REEF_GUARD_FINDINGS_KEY });
      const name = String(t(`measurement.history.parameterNames.${parameterId}`));
      const updated = data.find((i) => i.parameterId === parameterId);
      setAnnouncement(
        updated?.intervalHours == null
          ? String(t('testIntervals.announce.off', { parameter: name }))
          : String(t('testIntervals.announce.saved', { parameter: name, days: formatDays(updated.intervalHours, i18n.language) })),
      );
    },
  });

  let body;
  if (query.isPending) {
    body = <Text color="muted">{t('testIntervals.loading')}</Text>;
  } else if (query.isError) {
    body = (
      <LoadErrorState
        title={t('testIntervals.loadError')}
        onRetry={() => void query.refetch()}
        retryLabel={t('paramTargets.error.retry')}
        titleLevel={3}
      />
    );
  } else {
    body = (
      <Grid as="ul" minItemWidth="sm" gap="4" className="test-intervals__list" data-testid="test-intervals-list">
        {query.data.map((interval) => (
          <IntervalCard
            key={interval.parameterId}
            interval={interval}
            busy={save.isPending}
            onChange={(parameterId, value) => save.mutate({ parameterId, value })}
          />
        ))}
      </Grid>
    );
  }

  return (
    <Section id="test-intervals" title={t('testIntervals.title')} description={t('testIntervals.description')}>
      <Stack gap="4">
        <LiveRegion visuallyHidden busy={query.isPending}>{announcement}</LiveRegion>
        {save.isError && <Alert tone="danger">{t('testIntervals.saveError')}</Alert>}
        {body}
        <Text color="muted" variant="caption">{t('testIntervals.heuristicNote')}</Text>
      </Stack>
    </Section>
  );
}
